import React from "react";
import { Container, Section, PageHero, Card, Eyebrow, CTABand, SectionTitle } from "@/components/ui-bits/Primitives";
import LeadForm from "@/components/forms/LeadForm";
import { TrendingUp, Users, Leaf, Sparkles, Banknote, CheckCircle2 } from "lucide-react";
import { IMAGES } from "@/lib/site-data";

const BENEFITS = [
    { icon: Banknote, title: "New Revenue Stream", body: "Earn passive revenue from every charging session — no upfront capex required." },
    { icon: Users, title: "Increased Traffic", body: "Charging customers spend 25-45 min on-site, driving foot traffic to your retail tenants." },
    { icon: Leaf, title: "Sustainability Initiatives", body: "Meet ESG and corporate climate commitments with verifiable carbon-offset reporting." },
    { icon: Sparkles, title: "Future-Proof Assets", body: "EV-ready infrastructure protects property value as the fleet transitions accelerate." },
    { icon: TrendingUp, title: "Grant Eligibility", body: "We pursue NEVI, TCEQ, NCTCOG, and federal funding on your behalf — at no upfront cost." },
    { icon: CheckCircle2, title: "Turnkey Delivery", body: "We design, permit, build, own, and operate. You collect rent and host benefits." },
];

const FIELDS = [
    { name: "name", label: "Full Name", required: true, placeholder: "Jane Doe" },
    { name: "company", label: "Company / Property", placeholder: "Acme Holdings" },
    { name: "email", label: "Email", type: "email", required: true, placeholder: "you@company.com" },
    { name: "phone", label: "Phone", placeholder: "555-555-5555" },
    { name: "address", label: "Property Address", colSpan: 2, placeholder: "Street, City, State ZIP" },
    { name: "property_type", label: "Property Type", as: "select", options: ["Shopping Center", "Hotel", "Office", "Multifamily", "Healthcare", "Municipal", "Mixed-Use", "Other"] },
    { name: "parking_spaces", label: "Number of Parking Spaces", type: "number", placeholder: "e.g. 120" },
    { name: "electrical_service", label: "Existing Electrical Service", as: "select", options: ["Unknown", "<200A", "200A-400A", "400A-800A", "800A-1200A", ">1200A"] },
    { name: "message", label: "Additional Details", as: "textarea", colSpan: 2, placeholder: "Site context, timing, EV adoption goals…" },
];

export default function SiteHost() {
    return (
        <div data-testid="sitehost-page">
            <PageHero
                kicker="Site Host Program"
                title="Become a M-Class charging host."
                lead="Turn underutilized parking into a revenue-generating, customer-attracting, ESG-aligned asset — with zero capex and zero operations burden."
                image={IMAGES.commercial}
            >
                <a href="#apply" data-testid="sitehost-hero-cta" className="btn-primary">Apply to Host</a>
            </PageHero>

            <Section>
                <Container>
                    <SectionTitle kicker="Benefits" title="The host advantage." className="mb-12" />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {BENEFITS.map((b) => {
                            const I = b.icon;
                            return (
                                <Card key={b.title}>
                                    <I className="w-7 h-7 text-electric-400 mb-5" strokeWidth={1.5} />
                                    <h3 className="font-heading text-xl text-white mb-2">{b.title}</h3>
                                    <p className="text-sm text-slate-400 leading-relaxed">{b.body}</p>
                                </Card>
                            );
                        })}
                    </div>
                </Container>
            </Section>

            <Section id="apply" className="border-t border-white/5">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <Eyebrow className="mb-4">Application</Eyebrow>
                            <h2 className="font-heading font-medium text-white text-3xl md:text-4xl tracking-tight leading-[1.1]">Tell us about your property.</h2>
                            <p className="mt-6 text-slate-400 leading-relaxed">We'll review your site, run a preliminary utility & traffic analysis, and respond within one business day. Selected sites move into a no-cost feasibility study.</p>
                            <div className="mt-8 space-y-4">
                                {["No upfront capital required", "We pursue grants on your behalf", "Long-term host agreements", "Full ADA & American-vendor compliance"].map((s) => (
                                    <div key={s} className="flex items-center gap-3 text-slate-300 text-sm"><CheckCircle2 className="w-4 h-4 text-electric-400" /> {s}</div>
                                ))}
                            </div>
                        </div>
                        <div className="lg:col-span-7">
                            <Card hover={false} className="!p-8 md:!p-10">
                                <LeadForm type="siteHost" fields={FIELDS} submitLabel="Submit Application" testIdPrefix="sitehost" />
                            </Card>
                        </div>
                    </div>
                </Container>
            </Section>

            <CTABand title="Not sure if your site qualifies?" subtitle="Send us the address and we'll tell you — fast." primaryLabel="Contact Us" primaryTo="/contact" secondaryLabel="Explore Solutions" secondaryTo="/services" />
        </div>
    );
}
